import {
  EReportSegmentUrns,
  IReportCoaDimensionMetadata,
  IReportDatasetLens,
  IReportDimension,
  IReportLedgerType,
  SEMANTIC_TYPES
} from '@opengov/reporting-types';

export const selectReportCoaJoinColNameFn = (
  lens: IReportDatasetLens,
  coaDimension: IReportDimension
): string | undefined => {
  if (!coaDimension || !lens) {
    return;
  }
  let column = lens.columns.find((col) => col.joinedDimensionId && col.joinedDimensionId === coaDimension.id);

  if (!column) {
    column = lens.columns.find(
      (col) => col.semanticTypes && col.semanticTypes.indexOf(EReportSegmentUrns.accountNumberEbto) !== -1
    );

    if (!column) {
      column = lens.columns.find((col) =>
        col.semanticTypes?.find((semanticType) =>
          [EReportSegmentUrns.accountNumberId, EReportSegmentUrns.accountNumber].includes(
            semanticType as EReportSegmentUrns
          )
        )
      );
    }
  }

  return column?.name;
};

export const selectAccountTypeMapForCoaColumnsFn = (accountTypes: IReportLedgerType[]): Record<string, string> => {
  const columnNameToAccountType = {};

  if (accountTypes) {
    accountTypes.forEach((ledgerType) => {
      ledgerType.categoryLevels.forEach((catLevel) => {
        columnNameToAccountType[catLevel.columnRef] = ledgerType.label;
      });
    });
  }

  return columnNameToAccountType;
};

export const selectCoaDimensionColumnsFn = (
  coaDimension: IReportDimension,
  joinColName: string,
  includeAccountNumberAndEbtoLabels: boolean,
  isConsistentCoa: boolean
) => {
  const hideAccountNumberLabel = !includeAccountNumberAndEbtoLabels && !isConsistentCoa;
  const hideEbtoLabel = !includeAccountNumberAndEbtoLabels;

  return (
    coaDimension?.columns?.length > 0 &&
    coaDimension.columns
      .filter((column) => {
        if (
          !column ||
          column.semanticTypes.find((s) => s.includes(EReportSegmentUrns.accountNumberId)) ||
          column.semanticTypes.find((s) => s.includes(EReportSegmentUrns.accountNumber)) ||
          column.semanticTypes.find((s) => s.includes(EReportSegmentUrns.accountNumberEbto)) ||
          (hideAccountNumberLabel &&
            column.semanticTypes.includes('urn:opengov:types/semantic/display-delegate?column=account_number')) ||
          (hideEbtoLabel &&
            column.semanticTypes.includes('urn:opengov:types/semantic/display-delegate?column=account_number_ebto')) ||
          column.name.includes('order') ||
          column.label === 'FTE Counts' ||
          (column.name.match(/segment_object_.._cat_0/) && column.label !== 'Account Type') ||
          (!isConsistentCoa && column.semanticTypes.includes(SEMANTIC_TYPES.COA_ACCOUNT_STATUS))
        ) {
          return false;
        }
        return true;
      })
      .map((col) => {
        if (col.name.includes(':')) {
          return col;
        }
        const name = joinColName ? `${joinColName}:${col.name}` : col.name;
        return { ...col, name };
      })
  );
};

export const selectReportCoaDimensionMetadataFn = (
  dimension: IReportDimension,
  joinColumnName: string
): IReportCoaDimensionMetadata => {
  if (dimension?.dimensionMetadata?.content?.chartOfAccounts) {
    return {
      ledgerTypes: [],
      ...dimension.dimensionMetadata.content.chartOfAccounts,
      dimensionId: dimension.id,
      joinColumnName
    };
  }
};

const getCoaDimension = (lens: IReportDatasetLens): IReportDimension | undefined => {
  const coaColumn = lens.columns.find((c) => c.semanticTypes.includes(EReportSegmentUrns.accountNumberId));

  const coaDimension = lens.dimensions.find((d) => {
    if (!d.id.includes('/coa/')) return;
    return d.columns.find((col) => {
      return col.joinable && col.semanticTypes.includes(EReportSegmentUrns.accountNumberId);
    });
  });

  return coaColumn && coaDimension;
};

const getLegacyCoaDimension = (lens: IReportDatasetLens): IReportDimension | undefined => {
  const coaColumn = lens.columns.find((c) => c.semanticTypes.includes(EReportSegmentUrns.accountNumber));

  const coaDimension = lens.dimensions.find((d) => {
    if (!d.id.includes('/coa/')) return;
    return d.columns.find((col) => {
      return col.joinable && col.semanticTypes.includes(EReportSegmentUrns.accountNumber);
    });
  });

  return coaColumn && coaDimension;
};

const getCoaEbtoDimension = (lens: IReportDatasetLens): IReportDimension | undefined => {
  const coaEbtoColumn = lens.columns.find((c) => c.semanticTypes.includes(EReportSegmentUrns.accountNumberEbto));
  return coaEbtoColumn && lens.dimensions.find((d) => d.id.includes('/coa-ebto/'));
};

export const selectReportCoaDimensionFn = (lens: IReportDatasetLens): IReportDimension | undefined => {
  if (!lens) {
    return undefined;
  }

  const coaDimension = getCoaDimension(lens) ?? getLegacyCoaDimension(lens);

  if (coaDimension) {
    return coaDimension;
  }

  const coaEbtoDimension = getCoaEbtoDimension(lens);

  if (coaEbtoDimension) {
    return coaEbtoDimension;
  }

  return undefined;
};
